import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Globe, Clock, ChevronRight } from 'lucide-react';
import PageWrapper from '../components/PageWrapper';
import Breadcrumbs from '../components/Breadcrumbs';
import MedicalText from '../components/MedicalText';
import { commonConcernsArticles } from '../data/commonConcernsContent';

// Common Concerns Index Page
export const CommonConcernsIndex = () => {
  const navigate = useNavigate();

  return (
    <PageWrapper>
      <Breadcrumbs items={[
        { label: 'Education Library', path: '/education' },
        { label: 'Common Concerns', path: '/common-concerns' }
      ]} />

      <div className="page-header-centered" data-testid="common-concerns-index">
        <h1>Common Concerns</h1>
        <p>Patient education articles about frequently asked urological questions</p>
      </div>

      <div className="concerns-list" data-testid="concerns-list">
        {commonConcernsArticles.map(article => (
          <Link 
            to={`/common-concerns/${article.id}`}
            key={article.id}
            className="concern-list-item"
            data-testid={`concern-item-${article.id}`}
          >
            <div className="concern-item-content">
              <h3>{article.title.en}</h3>
              <p>{article.content.en.substring(0, 150).trim()}...</p>
            </div>
            <ChevronRight size={20} className="concern-item-arrow" />
          </Link>
        ))}
      </div>
    </PageWrapper>
  );
};

// Individual Article Page
const CommonConcernArticle = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [language, setLanguage] = useState('en');
  
  // Scroll to top when article loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [articleId]);
  
  const article = commonConcernsArticles.find(a => a.id === articleId);

  if (!article) {
    return (
      <PageWrapper>
        <div className="empty-state">
          <h2>Article Not Found</h2>
          <p>The article you&apos;re looking for doesn&apos;t exist.</p>
          <button 
            onClick={() => navigate('/common-concerns')} 
            className="btn-primary"
          >
            Back to Common Concerns
          </button>
        </div>
      </PageWrapper>
    );
  }

  const toggleLanguage = () => {
    setLanguage(lang => lang === 'en' ? 'es' : 'en');
  };

  // Calculate reading time
  const wordCount = article.content.en.split(' ').length;
  const readingTime = Math.ceil(wordCount / 200);

  // Map topic IDs to paths
  const topicPaths = {
    'prostate-biopsy': { path: '/after-visit/prostate-biopsy', title: 'Prostate Biopsy Aftercare' },
    'prostatitis': { path: '/learn/prostatitis', title: 'Prostatitis' },
    'prostate': { path: '/anatomy/prostate', title: 'Prostate Anatomy' },
    'uti': { path: '/after-visit/uti', title: 'Urinary Tract Infection' },
    'recurrent-uti': { path: '/after-visit/recurrent-uti', title: 'Recurrent UTI Prevention' },
    'bladder': { path: '/anatomy/bladder', title: 'Bladder Anatomy' },
    'testicle': { path: '/anatomy/testicle', title: 'Testicle Anatomy' },
    'hydrocele-spermatocele': { path: '/learn/hydrocele-spermatocele', title: 'Hydrocele & Spermatocele' },
    'testicular-cancer': { path: '/learn/testicular-cancer', title: 'Testicular Cancer' },
    'pelvic-floor-pt': { path: '/education/pelvic-floor-pt', title: 'Pelvic Floor Physical Therapy' },
    'erectile-dysfunction': { path: '/after-visit/erectile-dysfunction', title: 'Erectile Dysfunction' },
    'bph': { path: '/after-visit/bph', title: 'BPH (Enlarged Prostate)' },
    'overactive-bladder': { path: '/learn/overactive-bladder', title: 'Overactive Bladder' },
    'urethra': { path: '/anatomy/urethra', title: 'Urethra Anatomy' },
    'genital-warts': { path: '/common-concerns/genital-warts', title: 'Genital Warts' },
    'testicular-pain': { path: '/after-visit/testicular-pain', title: 'Testicular Pain' },
    'urethritis': { path: '/common-concerns/urethritis', title: 'Urethritis' },
    'varicocele': { path: '/learn/varicocele', title: 'Varicocele' },
    'male-infertility': { path: '/learn/male-infertility', title: 'Male Infertility' },
    'catheter-removal': { path: '/after-visit/catheter-removal', title: 'Catheter Removal' },
    'kidney-stone-conservative': { path: '/after-visit/kidney-stone-conservative', title: 'Kidney Stone Care' },
    'hematuria': { path: '/after-visit/hematuria', title: 'Blood in Urine' }
  };

  // Parse inline bold text (converts **text** to <strong>text</strong>)
  const parseBoldText = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={idx}>{part.slice(2, -2)}</strong>;
      }
      return <MedicalText key={idx}>{part}</MedicalText>;
    });
  };

  // Render content with markdown-style formatting
  const renderContent = (content) => {
    const lines = content.split('\n');
    const elements = [];
    let currentParagraph = [];

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      
      // Handle horizontal divider
      if (trimmedLine === '---') {
        if (currentParagraph.length > 0) {
          elements.push(
            <p key={`p-${index}`}>{parseBoldText(currentParagraph.join(' '))}</p>
          );
          currentParagraph = [];
        }
        elements.push(<hr key={`hr-${index}`} className="article-divider" />);
        return;
      }
      
      // Handle empty lines (paragraph break)
      if (trimmedLine === '') {
        if (currentParagraph.length > 0) {
          elements.push(
            <p key={`p-${index}`}>{parseBoldText(currentParagraph.join(' '))}</p>
          );
          currentParagraph = [];
        }
        return;
      }
      
      // Handle bold header lines (entire line is **text**)
      if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**') && !trimmedLine.slice(2, -2).includes('**')) {
        if (currentParagraph.length > 0) {
          elements.push(
            <p key={`p-${index}`}>{parseBoldText(currentParagraph.join(' '))}</p>
          );
          currentParagraph = [];
        }
        const headerText = trimmedLine.slice(2, -2);
        elements.push(
          <h3 key={`h-${index}`} className="article-section-header">
            <MedicalText>{headerText}</MedicalText>
          </h3>
        );
        return;
      }
      
      // Handle bullet points
      if (trimmedLine.startsWith('•') || trimmedLine.startsWith('- ')) {
        if (currentParagraph.length > 0) {
          elements.push(
            <p key={`p-${index}`}>{parseBoldText(currentParagraph.join(' '))}</p>
          );
          currentParagraph = [];
        }
        const bulletText = trimmedLine.replace(/^[•-]\s*/, '');
        elements.push(
          <div key={`li-${index}`} className="article-list-item">
            <span className="list-bullet">•</span>
            <span>{parseBoldText(bulletText)}</span>
          </div>
        );
        return;
      }
      
      // Regular text - add to current paragraph
      currentParagraph.push(trimmedLine);
    });

    // Don't forget remaining paragraph
    if (currentParagraph.length > 0) {
      elements.push(
        <p key="p-final">{parseBoldText(currentParagraph.join(' '))}</p>
      );
    }

    return elements;
  };

  return (
    <PageWrapper>
      <Breadcrumbs items={[
        { label: 'Education Library', path: '/education' },
        { label: 'Common Concerns', path: '/common-concerns' },
        { label: article.title.en, path: `/common-concerns/${article.id}` }
      ]} />

      <article className="article-full" data-testid="concern-article">
        {/* Header - matches article-header style */}
        <header className="article-header">
          <h1>{article.title[language]}</h1>
          <div className="article-meta">
            <span><Clock size={16} /> {readingTime} min read</span>
            <button 
              onClick={toggleLanguage}
              className="language-btn"
              data-testid="language-toggle"
            >
              <Globe size={16} />
              {language === 'en' ? 'Ver en Español' : 'View in English'}
            </button>
          </div>
        </header>

        {/* Article Image */}
        {article.image && (
          <div className="article-image-container" data-testid="article-image">
            <img 
              src={article.image.url} 
              alt={article.image.alt[language]}
              className="article-main-image"
            />
            {article.image.caption && (
              <figcaption className="article-image-caption">
                <span className="caption-text">{article.image.caption[language]}</span>
                <span className="caption-source">{article.image.source}</span>
              </figcaption>
            )}
          </div>
        )}

        {/* Body - matches article-body style */}
        <div className="article-body">
          {renderContent(article.content[language])}

          {/* When to Call Your Doctor */}
          <div className="warning-callout" data-testid="when-to-call">
            <div className="warning-callout-header">
              <AlertTriangle size={18} />
              <h3>{language === 'en' ? 'When to Seek Care' : 'Cuándo Buscar Atención'}</h3>
            </div>
            <ul>
              {article.whenToCall[language].map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </div>

          {/* Related Topics */}
          {article.relatedTopics && article.relatedTopics.length > 0 && (
            <div className="related-section">
              <h3>{language === 'en' ? 'Related Topics' : 'Temas Relacionados'}</h3>
              <div className="related-chips">
                {article.relatedTopics.map((topicId, idx) => {
                  const topic = topicPaths[topicId];
                  if (!topic) return null;
                  return (
                    <Link key={idx} to={topic.path} className="related-chip">
                      {topic.title}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </article>
    </PageWrapper>
  );
};

export default CommonConcernArticle;
